/**
 * Manifest is a JSON file stored at `__manifest.json` in the B2 bucket. The
 * server exposes /api/manifest GET/PUT which read and write it (via SigV4).
 *
 * Entries:
 *   { id, name, mime, size, storage, b2Key, width?, height?, uploadedAt }
 *   Notes are special-cased with `storage: "note-inline"` and a `content`
 *   string (no bytes to fetch separately).
 */

let state = { files: [] };

async function readJson(res) {
  try { return await res.json(); } catch { return {}; }
}

async function api(url, opts) {
  const res = await fetch(url, opts);
  const json = await readJson(res);
  if (!res.ok || !json.ok) {
    throw new Error(json.error || `${url} failed (${res.status})`);
  }
  return json;
}

export function compactManifestEntry(entry) {
  if (entry.storage === "note-inline" || entry.kind === "note" || !entry.content) {
    return entry;
  }
  // Strip inline preview text for non-note files — kept in memory only.
  const { content, ...rest } = entry;
  return rest;
}

export function compactManifestFiles(files) {
  return files.map(compactManifestEntry);
}

export async function loadManifest() {
  const res = await api("/api/manifest");
  state.files = compactManifestFiles(res.files || []);
  return state.files;
}

async function save() {
  const files = compactManifestFiles(state.files);
  await api("/api/manifest", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ files }),
  });
}

export async function addFile(entry) {
  state.files = [entry, ...state.files];
  await save();
  return state.files;
}

export async function removeFile(id) {
  const target = state.files.find((f) => f.id === id);
  state.files = state.files.filter((f) => f.id !== id);
  await save();
  if (target && target.storage === "b2" && target.b2Key) {
    // Best-effort B2 object cleanup. If it fails the manifest is already
    // updated, so the file is unreachable from the UI — worst case is an
    // orphaned blob until manual cleanup.
    try {
      await fetch(`/api/b2/file?key=${encodeURIComponent(target.b2Key)}`, { method: "DELETE" });
    } catch {}
  }
  return state.files;
}

export async function reorder(orderedIds) {
  const map = new Map(state.files.map((f) => [f.id, f]));
  const next = orderedIds.map((id) => map.get(id)).filter(Boolean);
  if (next.length === state.files.length) {
    state.files = next;
    await save();
  }
  return state.files;
}

export function getFiles() {
  return state.files;
}

// ─── Storage usage ───────────────────────────────────────────────────────────
// B2 free tier is 10 GB. We cap usable storage one notch under that (9 GB) so
// the manifest, headers, and any short-lived overhead never tip the bucket
// into paid territory.
export const STORAGE_LIMIT_BYTES = 9 * 1024 * 1024 * 1024;

export function getStorageUsage(files) {
  const used = files.reduce((sum, f) => sum + (f.size || 0), 0);
  const limit = STORAGE_LIMIT_BYTES;
  const remaining = Math.max(0, limit - used);
  const pct = Math.min(100, (used / limit) * 100);
  return {
    used,
    limit,
    remaining,
    pct,
    isWarning: pct >= 75,
    isCritical: pct >= 90,
    isFull: used >= limit,
  };
}
